import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/i18n";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "work", label: t.nav.work },
    { id: "services", label: t.nav.services },
    { id: "approach", label: t.nav.approach },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-bg/70 backdrop-blur-xl border-b border-line" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto max-w-[1400px] px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("top")}
          className="flex items-center group"
          data-testid="nav-logo"
        >
          <img src={IMAGES.logoFull} alt="CHEPL — Check-in with people" className="h-14 w-auto object-contain" />
        </button>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="text-sm text-ink-2 hover:text-ink transition-colors duration-300 relative group"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand group-hover:w-full transition-[width] duration-300" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={toggle}
            className="font-mono text-xs tracking-widest uppercase text-ink-2 hover:text-brand transition-colors duration-300 border border-line hover:border-brand rounded-full px-3 py-1.5"
            data-testid="lang-toggle"
          >
            {lang === "nl" ? "NL / EN" : "EN / NL"}
          </button>
          <button
            onClick={() => scrollTo("contact")}
            className="hidden sm:inline-flex items-center bg-brand hover:bg-brand-hover text-white font-medium text-sm rounded-full px-5 py-2.5 transition-colors duration-300 active:scale-95"
            data-testid="nav-cta"
          >
            {t.nav.cta}
          </button>
          <button
            className="md:hidden text-ink"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            <div className="space-y-1.5">
              <span className={`block h-0.5 w-6 bg-ink transition-transform ${open ? "translate-y-2 rotate-45" : ""}`} />
              <span className={`block h-0.5 w-6 bg-ink transition-opacity ${open ? "opacity-0" : ""}`} />
              <span className={`block h-0.5 w-6 bg-ink transition-transform ${open ? "-translate-y-2 -rotate-45" : ""}`} />
            </div>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-bg/95 backdrop-blur-xl border-b border-line"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  className="text-left text-lg font-head text-ink"
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
